import type { CountyAdapter, ParsedDocument } from "../types";

interface DeltaBuilder extends ParsedDocument {
  hasReferral: boolean;
  parcelIds: Set<string>;
  bookPage: string;
  legalParts: string[];
}

const parseDate = (s: string): string => {
  const p = s.trim().split("/");
  if (p.length === 3) {
    const [m, d, y] = p;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  return s;
};

const parse = (text: string): ParsedDocument[] => {
  const lines = text.split("\n");
  const docs = new Map<string, DeltaBuilder>();
  let i = 0;

  // Header: date  book-page  [optional second id]  document type
  const headerRe = /^\s*(\d{2}\/\d{2}\/\d{4})\s+(\d{5}-\d{5})(?:\s+\S+)?\s+([A-Z][A-Z &\/-]+?)\s*$/;
  const parcelRe = /(\d{3}-\d{3}-\d{3}-\d{3}-\d{2})/;

  while (i < lines.length) {
    const line = lines[i].trimEnd();
    const h = line.match(headerRe);
    if (!h) {
      i++;
      continue;
    }
    const filingDate = h[1];
    const bookPage = h[2];
    const documentType = h[3].trim();

    const blockLines: string[] = [];
    let referralLine = "";
    i++;
    while (i < lines.length) {
      const next = lines[i];
      if (next.match(headerRe)) break;
      if (next.includes(">>>>>>")) {
        referralLine = next;
        blockLines.push(next);
        i++;
        continue;
      }
      blockLines.push(next);
      i++;
    }

    const hasReferral = referralLine.toUpperCase().includes("REFERRAL");

    if (!docs.has(bookPage)) {
      docs.set(bookPage, {
        documentNumber: bookPage,
        documentType,
        filingDate: parseDate(filingDate),
        grantors: [],
        grantees: [],
        legalParts: [],
        hasReferral: false,
        parcelIds: new Set<string>(),
        bookPage,
      });
    }
    const doc = docs.get(bookPage)!;
    if (hasReferral) doc.hasReferral = true;

    for (const bl of blockLines) {
      const t = bl.trim();
      if (!t) continue;

      const pm = bl.match(parcelRe);
      if (pm) doc.parcelIds.add(pm[1]);

      // strip leading "X " marker
      const cleaned = t.replace(/^X\s+/i, "");

      const seller = cleaned.match(/^SELLER\s+(.*)/i);
      if (seller) {
        // strip trailing role tokens like ", MARRIED PERSON", ", HUSBAND", ", WIFE"
        const n = seller[1].replace(/,\s*(MARRIED PERSON|HUSBAND|WIFE|SINGLE PERSON|SINGLE|UNMARRIED).*$/i, "").trim();
        if (n && !doc.grantors.includes(n)) doc.grantors.push(n);
        continue;
      }
      const purchaser = cleaned.match(/^PURCHASER\s+(.*)/i);
      if (purchaser) {
        const n = purchaser[1].replace(/,\s*(MARRIED PERSON|HUSBAND|WIFE|SINGLE PERSON|SINGLE|UNMARRIED).*$/i, "").trim();
        if (n && !doc.grantees.includes(n)) doc.grantees.push(n);
        continue;
      }
      if (cleaned.startsWith(">>>>>>")) continue;
      if (cleaned.match(/^(SEC\s+\d|LOT\s|LOTS\s)/i)) {
        if (!doc.legalParts.includes(cleaned)) doc.legalParts.push(cleaned);
        continue;
      }
      if (cleaned.match(/\bTOWNSHIP\b|\bCITY OF\b|\bVILLAGE OF\b|\bSUB\b|\bCONDOS?\b|\bESTATES?\b|\bADDITION\b|\bPLAT\b/i)) {
        if (!doc.legalParts.includes(cleaned)) doc.legalParts.push(cleaned);
      }
    }
  }

  return Array.from(docs.values()).map((d) => ({
    documentNumber: d.documentNumber,
    documentType: d.documentType,
    filingDate: d.filingDate,
    grantors: d.grantors,
    grantees: d.grantees,
    legalDescription: d.legalParts.join(" | ") || undefined,
    notes: [
      d.parcelIds.size ? `Parcel(s): ${Array.from(d.parcelIds).join(", ")}` : null,
      `Book-Page: ${d.bookPage}`,
    ]
      .filter(Boolean)
      .join(" | "),
    skipReason: d.hasReferral ? "referral" : undefined,
  }));
};

export const deltaAdapter: CountyAdapter = {
  countyId: "delta",
  countyLabel: "Delta County, MI - Land Contract Parser",
  description:
    "Paste search results from Delta County (MI) Register of Deeds. Consolidates multiple SELLER/PURCHASER rows per document, filters referrals, family transfers, business-to-business, and bank transactions.",
  placeholder: `Paste Delta County search results here...

Expected format (multi-line blocks per document):
   05/21/2021    01308-00200   1-20420           LAND CONTRACT
   SELLER           WHALE THOMAS, MARRIED PERSON
 X PURCHASER        CAVANAUGH WILLIAM
   SEC 03  T40N  R19W ...                                                       NAHMA TOWNSHIP
>>>>>> ADDITIONAL PARTIES

Records with "REFERRAL" in the >>>>>> line will be excluded.`,
  detectionReasons: ["Land Contract", "Delta County MI ROD"],
  filterRules: [
    "Referrals: Records with \"REFERRAL\" in the additional info line are filtered out",
    "Family transfers: Same last name on seller/purchaser sides are filtered out",
    "Business-to-business: Both parties are entities (LLC, Inc, Trust, etc.) are filtered out",
    "Bank transactions: Any party is a bank/mortgage company → filtered out",
    "Church/IRA: Any party is a church, ministry, or IRA → filtered out",
    "Duplicates: Multiple blocks per book-page document are consolidated into one record",
  ],
  parse,
};
